import {
  useClientsData,
  useExchangesMetadata,
  useUser,
} from "@shared/utils/hooks/misc";
import { useContext, useMemo } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import { ToggleField } from "@shared/features/bots/BotForm/common/types";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { BotType } from "@shared/types";
import { useAccountAlternativeUsdPairs } from "@shared/features/configuration/hooks";

interface useGroupMainCoinsSignalsCountProps {
  isBacktestModal?: boolean;
  timeframe?: {
    start: number | null;
    end: number | null;
  };
}

const useGroupMainCoinsSignalsCount = ({
  isBacktestModal,
  timeframe,
}: useGroupMainCoinsSignalsCountProps) => {
  const { user } = useUser();
  const {
    subGroup,
    account,
    alternativeUsdPairs,
    checkedFields,
    botType,
    followFuturesAsSpot,
  } = useContext(BotFormContext)!;
  const { getAlternativeUsdPairs, isSuccess: isExchangesMetadataSuccess } =
    useExchangesMetadata();
  const { activeClientsData, isSuccess: isClientsDataSuccess } =
    useClientsData();
  const { getAccountData } = useAccountAlternativeUsdPairs();

  const parsedAccount = isSymbol(account) ? undefined : account;
  const parsedAlternativePairs =
    isSymbol(alternativeUsdPairs) ||
    !checkedFields.has(ToggleField.ALTERNATIVE_USD_PAIRS)
      ? undefined
      : alternativeUsdPairs;
  const exchange = parsedAccount?.exchange;
  const isOnboarding = user?.is_onboarding;
  const isSignalsBot = botType === BotType.SIGNAL;

  const onboardingArgs = useMemo(() => {
    if (
      !isSignalsBot ||
      !isClientsDataSuccess ||
      !isExchangesMetadataSuccess ||
      !isOnboarding ||
      !activeClientsData?.length ||
      !subGroup
    ) {
      return skipToken;
    }
    const exchangeToAlternativePairs = activeClientsData.reduce(
      (acc, account) => {
        const exchange = account.exchange;
        if (getAccountData(account).isDefaultEnabled) {
          acc[exchange] = getAlternativeUsdPairs(account.client_id) || [];
        } else {
          acc[exchange] = null;
        }
        return acc;
      },
      {} as Record<string, string[] | null>
    );
    return {
      sub_group_id: subGroup.id,
      exchange_to_alternative_usd_pairs: exchangeToAlternativePairs,
      follow_futures_as_spot: followFuturesAsSpot === true,
    };
  }, [
    isSignalsBot,
    followFuturesAsSpot,
    isOnboarding,
    isClientsDataSuccess,
    isExchangesMetadataSuccess,
    activeClientsData,
    subGroup,
  ]);

  const botFormArgs = useMemo(() => {
    const isInvalidBacktestingTimeframe =
      isBacktestModal && (!timeframe || !timeframe?.start || !timeframe?.end);
    if (
      !isSignalsBot ||
      !subGroup ||
      !exchange ||
      isInvalidBacktestingTimeframe
    ) {
      return skipToken;
    }
    return {
      sub_group_id: subGroup.id,
      follow_futures_as_spot: followFuturesAsSpot === true,
      exchange_to_alternative_usd_pairs: {
        [exchange]: parsedAlternativePairs ?? null,
      },
      time_range: timeframe as
        | {
            start: number;
            end: number;
          }
        | undefined,
    };
  }, [
    subGroup,
    exchange,
    timeframe,
    parsedAlternativePairs,
    followFuturesAsSpot,
  ]);

  const {
    currentData: exactMainCoinsToSignalsCount,
    isFetching,
    isLoading,
    isError,
  } = sharedLib.sharedApi.bots.useGetGroupMainCoinsToSignalsCountMultipleExchangesQuery(
    isOnboarding ? onboardingArgs : botFormArgs
  );
  const { currentData: onboardingGroupData } =
    sharedLib.sharedApi.common.useGetGroupDataQuery(
      isOnboarding && isSignalsBot && subGroup
        ? { sub_group_id: subGroup.id }
        : skipToken
    );
  const onboardingSignalsCount =
    onboardingGroupData?.exchange_to_main_coin_to_last_month_signals_count;
  const isUsingOnboardingSignalsCount =
    !exactMainCoinsToSignalsCount && !!onboardingSignalsCount;

  return {
    mainCoinsToSignalsCount:
      exactMainCoinsToSignalsCount ?? onboardingSignalsCount,
    hasExactSignalsCount: !!exactMainCoinsToSignalsCount,
    isFetching: isFetching && !isUsingOnboardingSignalsCount,
    isLoading: isLoading && !isUsingOnboardingSignalsCount,
    isError,
  };
};

export default useGroupMainCoinsSignalsCount;
